import * as Sentry from '@sentry/react'

import { env } from '~publish/env'

const SENTRY_DSN =
  'https://ecc425b09a12c9dbce6b133169083eb4@o4510763174920192.ingest.us.sentry.io/4510763176361984'

export const initSentry = (): void => {
  if (env.VITE_NODE_ENV === 'test') {
    return
  }

  const isProduction = env.VITE_NODE_ENV === 'production'

  const release =
    env.VITE_COMMIT_HASH && env.VITE_COMMIT_HASH !== 'undefined'
      ? env.VITE_COMMIT_HASH
      : 'unknown'

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: env.VITE_NODE_ENV,
    release: `publish-frontend@${release}`,

    integrations: [Sentry.browserTracingIntegration()],

    sendDefaultPii: false,

    sampleRate: isProduction ? 0.25 : 1,
    // `undefined` not 0 off prod: `0` is not nullish, so it keeps tracing on.
    tracesSampleRate: isProduction ? 0.001 : undefined,

    ignoreErrors: [
      // Browser/environment noise
      'ResizeObserver loop',
      // Browser extension errors (can surface from page context, bypassing denyUrls)
      'runtime.sendMessage',
      'Extension context invalidated',
      // Network errors (usually transient)
      'Network request failed',
      'Load failed',
      'Failed to fetch',
      'TypeError: cancelled',
      'TypeError: NetworkError',
      'AbortError',
      // Cross-origin script errors with no useful info
      'Script error.',
      // Non-error values thrown as exceptions
      'Non-Error promise rejection',
      /^undefined$/,
    ],

    allowUrls: [/https?:\/\/(.*\.)?buffer\.com/],

    denyUrls: [/^chrome-extension:\/\//, /^moz-extension:\/\//],

    beforeSend: sentryBeforeSend,
  })
}

type SentryEvent = Sentry.ErrorEvent
type SentryHint = Sentry.EventHint

/**
 * Exception-carrying events must wrap a real `Error`; message-only events
 * (deliberate `captureMessage`/`captureEvent` calls, e.g. the composer bridge
 * drift reports) carry no Error in the hint and must pass through untouched.
 */
export function sentryBeforeSend(
  event: SentryEvent,
  hint: SentryHint,
): SentryEvent | null {
  const error = hint.originalException

  // Drop thrown non-Error values (string throws, undefined rejections).
  if (event.exception && !(error instanceof Error)) {
    return null
  }

  // Group chunk load failures into a single issue
  if (
    error instanceof Error &&
    error.message.match(/Loading chunk .* failed/)
  ) {
    event.fingerprint = ['chunk-load-failure']
  }

  // Group GraphQL errors by operation name, isolating known error categories
  // into their own stable issue (e.g. pagination_limit). Unknown GraphQL errors
  // retain the default grouping so Sentry can still split them by stacktrace.
  if (event.tags?.source === 'apollo' && event.tags?.operation) {
    const category = event.tags.graphql_error_category
    event.fingerprint =
      category && category !== 'unknown'
        ? ['graphql', String(event.tags.operation), String(category)]
        : ['graphql', String(event.tags.operation), '{{ default }}']
  }

  // React context-guard invariants (e.g. "useSelectedChannel must be used
  // within a ChannelProvider") default-group by stacktrace, which splits the
  // same guard into a different issue per route. Tag and fingerprint them
  // uniformly so one Sentry alert covers every guard hook, present and future.
  // Require a capitalized Provider/component identifier after the phrase
  // (the shape every guard in this repo uses) so an unrelated error that
  // merely contains the substring isn't miscategorized.
  if (
    error instanceof Error &&
    error.message.match(/must be used within (?:an? )?<?[A-Z]/)
  ) {
    event.tags = { ...event.tags, error_category: 'missing_context_provider' }
    event.fingerprint = ['missing-context-provider', error.message]
  }

  return event
}

export function setSentryUser(userId: string | null): void {
  if (userId) {
    Sentry.setUser({ id: userId })
  } else {
    Sentry.setUser(null)
  }
}

// Export Sentry for use in other modules
export { Sentry }
